import {skipToken} from '@reduxjs/toolkit/query';
import {Alert, DataStatus} from 'platform/components';
import {Heading, Show, Space, Text} from 'platform/foundation';

import {Trans} from 'react-i18next';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {
  useGetRequestCountsQuery,
  useGetRequestQuery,
  useUpdateRequestMutation,
} from '../../../store/priceReportApi';

interface WelcomeSidebarProps extends TestIdProps {
  requestId?: string;
}

export function WelcomeSidebar(props: WelcomeSidebarProps) {
  const {data: request} = useGetRequestQuery(
    isNotNil(props.requestId) ? {id: props.requestId} : skipToken
  );
  const shouldSkipRequestCounts = isNilOrEmpty(request?.vehicle?.make);
  const {currentData: requestCounts, isFetching: isRequestCountsFetching} =
    useGetRequestCountsQuery(isNotNil(props.requestId) ? {id: props.requestId} : skipToken, {
      skip: shouldSkipRequestCounts,
    });
  const [, {isLoading: isUpdateRequestLoading}] = useUpdateRequestMutation({
    fixedCacheKey: 'create-page-update',
  });

  const count = isNotNil(requestCounts)
    ? (requestCounts.sold ?? 0) + (requestCounts.stock ?? 0)
    : null;

  return (
    <>
      <Heading size={3}>{i18n.t('page.create.labels.welcomeToPriceReport')}</Heading>
      <Space vertical={6} />
      <Text size="small">
        <Trans
          i18nKey="page.create.labels.about1Paragraph"
          components={{
            bold: <Text key="bold" size="small" alternative inline />,
          }}
        />
      </Text>
      <Space vertical={4} />
      <Show when={isNotNil(count)}>
        <Text size="small">{i18n.t('page.create.labels.about2Paragraph')}</Text>
        <Space vertical={6} />
        <DataStatus isLoading={isRequestCountsFetching || isUpdateRequestLoading} minHeight={15}>
          <Show when={isNotNil(count) && count > 0}>
            <Alert
              type="inline"
              variant="info"
              title={i18n.t('page.create.labels.similarVehicles', {
                count: count ?? 0,
              })}
              data-testid={suffixTestId('similarVehicles', props)}
            />
          </Show>
          <Show when={count === 0}>
            <Alert
              type="inline"
              variant="warning"
              title={i18n.t('page.create.labels.vehicleNotFound')}
              data-testid={suffixTestId('noVehicleFound', props)}
            />
          </Show>
        </DataStatus>
      </Show>
    </>
  );
}
